import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { daysOfWeek, visitTypes } from './schedulingConstants';

export function CreateTemplateDialog({ 
  isOpen, 
  onOpenChange, 
  staff, 
  clients, 
  onSubmit 
}) {
  const [templateForm, setTemplateForm] = useState({
    name: '',
    staff_id: '',
    client_id: '',
    start_time: '09:00',
    end_time: '10:00',
    visit_type: 'personal_care',
    tasks: [],
    notes: '',
    days_of_week: [],
  });
  const [newTask, setNewTask] = useState('');

  const handleAddTask = () => {
    if (newTask.trim()) {
      setTemplateForm(prev => ({
        ...prev,
        tasks: [...prev.tasks, newTask.trim()]
      }));
      setNewTask('');
    }
  };

  const handleRemoveTask = (index) => {
    setTemplateForm(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_, i) => i !== index)
    }));
  };

  const handleToggleDay = (dayId) => {
    setTemplateForm(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(dayId)
        ? prev.days_of_week.filter(d => d !== dayId)
        : [...prev.days_of_week, dayId].sort()
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(templateForm);
    setTemplateForm({
      name: '',
      staff_id: '',
      client_id: '',
      start_time: '09:00',
      end_time: '10:00',
      visit_type: 'personal_care',
      tasks: [],
      notes: '',
      days_of_week: [],
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-teal-600 hover:bg-teal-700" data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Recurring Template</DialogTitle>
          <DialogDescription>Set up a weekly visit pattern</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Template Name *</Label>
            <Input
              value={templateForm.name}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Morning Medication - Mrs Smith"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Care Worker *</Label>
              <Select value={templateForm.staff_id} onValueChange={(v) => setTemplateForm(prev => ({ ...prev, staff_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select staff" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.user?.first_name} {s.user?.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={templateForm.client_id} onValueChange={(v) => setTemplateForm(prev => ({ ...prev, client_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.first_name} {c.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input
                type="time"
                value={templateForm.start_time}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, start_time: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <Input
                type="time"
                value={templateForm.end_time}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, end_time: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Visit Type</Label>
              <Select value={templateForm.visit_type} onValueChange={(v) => setTemplateForm(prev => ({ ...prev, visit_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {visitTypes.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Days of Week *</Label>
            <div className="flex flex-wrap gap-2">
              {daysOfWeek.map(day => (
                <Button
                  key={day.id}
                  type="button"
                  variant={templateForm.days_of_week.includes(day.id) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleToggleDay(day.id)}
                  className={templateForm.days_of_week.includes(day.id) ? 'bg-teal-600 hover:bg-teal-700' : ''}
                >
                  {day.short}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Tasks</Label>
            <div className="flex gap-2">
              <Input
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="Add a task..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTask())}
              />
              <Button type="button" variant="outline" onClick={handleAddTask}>Add</Button>
            </div>
            {templateForm.tasks.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {templateForm.tasks.map((task, i) => (
                  <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => handleRemoveTask(i)}>
                    {task} ×
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700">Create Template</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
